import { useT } from "../LangContext";

// ─── Fase 17B · Bloque 1 — Step-based navigation bar ───────────────────
// The numbered steps ARE the navigation. Four steps map to four section ids:
//   ① chamber · ② strategy · ③ lab · ④ oracle
// HELP, the business simulator (Sep 2026) and the in-app guide button live on
// the right, behind a vertical divider, outside of the numbered flow. CUENTA is no longer in this bar:
// it moved to the Header (see Phase 17B · Block 3).
//
// Each step renders: circle-number (or brass ✓ when done) · action label ·
// connector line. The first incomplete step gets the "next" treatment
// (brass dot inside the circle). Re-clicking step ④ while already on RESULTS
// triggers `onResultsReclick` (scroll to the decision checklist).
//
// Mobile: under ~480 px viewport the action labels collapse — only the
// active step keeps its label, the rest show just the circle.

export default function StepNav({
  activeTab,
  hasPlan,
  hasStrategy,
  labAnalyzed,
  hasResults,
  onNavigate,
  onResultsReclick,
  guideOpen,
  onToggleGuide,
}) {
  const { t } = useT();

  const STEPS = [
    { id: "chamber",  num: 1, label: t("stepnav_1"), done: hasPlan },
    { id: "strategy", num: 2, label: t("stepnav_2"), done: hasStrategy },
    { id: "lab",      num: 3, label: t("stepnav_3"), done: !!labAnalyzed },
    { id: "oracle",   num: 4, label: t("stepnav_4"), done: hasResults },
  ];
  const nextIdx = STEPS.findIndex(s => !s.done);

  const handleClick = (id) => {
    if (id === "oracle" && activeTab === "oracle" && onResultsReclick) {
      onResultsReclick();
      return;
    }
    onNavigate(id);
  };

  return (
    <div className="pf-stepnav-wrap">
      <div className="pf-stepnav" role="tablist" data-testid="step-nav">
        {STEPS.map((step, i) => {
          const isActive = activeTab === step.id;
          const isDone = step.done === true;
          const isNext = !isDone && i === nextIdx;
          const status = isDone ? "done" : (isNext ? "next" : "todo");
          return (
            <div key={step.id} className="pf-step-item" data-status={status}>
              <button
                className={`pf-step ${isActive ? "active" : ""}`}
                data-testid={`step-${step.id}`}
                data-status={status}
                onClick={() => handleClick(step.id)}
                aria-current={isActive ? "step" : undefined}
              >
                <span className="pf-step-circle" aria-hidden="true">
                  {isDone ? "✓" : (isNext ? "·" : step.num)}
                </span>
                <span className="pf-step-label">{step.label}</span>
              </button>
              {i < STEPS.length - 1 && (
                <span className="pf-step-connector" aria-hidden="true" />
              )}
            </div>
          );
        })}

        <div className="pf-stepnav-right">
          {/* Sep 2026 — business simulator: outside the numbered flow */}
          <button
            className={`pf-stepnav-extra pf-stepnav-biz ${activeTab === "business" ? "active" : ""}`}
            data-testid="step-business"
            onClick={() => onNavigate("business")}
          >
            <span aria-hidden="true">◆</span> {t("biz_nav")}
            <span className="pf-new-dot">{t("biz_new")}</span>
          </button>
          <button
            className={`pf-stepnav-extra ${activeTab === "gloss" ? "active" : ""}`}
            data-testid="step-help"
            onClick={() => onNavigate("gloss")}
          >
            {t("tab_help")}
          </button>
          <button
            className="pf-stepnav-extra pf-stepnav-guide"
            data-testid="stepnav-guide-toggle"
            onClick={onToggleGuide}
            aria-expanded={guideOpen}
            aria-label={t("tabs_guide_button")}
            title={t("tabs_guide_button")}
          >
            <span aria-hidden="true">{guideOpen ? "▾" : "ⓘ"}</span> {t("tabs_guide_button")}
          </button>
        </div>
      </div>
    </div>
  );
}
